import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useBlockCatalogue, openBlocks, blockAgeDays, promiseState } from "@/lib/blockCatalogue";
import { useOwnerColours } from "./OwnerSplit";

/**
 * Blocked Board widget — the hero's right-hand panel (replaces the BLOCKED
 * counter it used to carry).
 *
 * A counter can say "6". It cannot say whether that is six customers sitting on
 * drawings or one machine down. This lists the owners we're waiting on, ranked,
 * outside first — the same order the Blocked Board itself uses, so the widget
 * reads as a preview of the page it opens rather than a separate summary.
 *
 * Computed from the items already in memory, so it costs no extra fetch.
 *
 * Calm "big number + owners" layout (Tushar, 3-Nov-2026, mock option B in
 * plans/blocked-widget-mock.html): titled "Blocked Board" so it's obvious what
 * it opens; a big captioned item count on the left (reads like the hero's
 * KPIs); owners on the right, each with a thin bar scaled to the busiest
 * owner — ambers outside Rockpecker, greys inside, dashed zinc for "no owner
 * yet" (shared colours from OwnerSplit). Red is kept to one small dot so the
 * card takes tension away from the hero rather than adding to it.
 */
const MAX_ROWS = 5;

export default function BlockedHeroWidget({ items }) {
  const navigate = useNavigate();
  const { catalogue } = useBlockCatalogue();

  const { rollup, total, itemCount, overdue } = useMemo(() => {
    const acc = {};
    const blockedItems = new Set();
    let n = 0, od = 0;
    (items || []).forEach((it) => {
      if (it.stage === "Completed") return;
      openBlocks(it).forEach((b) => {
        const key = b.owner || "_none";
        const meta = catalogue?.owners?.[b.owner];
        const r = acc[key] || (acc[key] = {
          key, owner: b.owner, label: meta?.label || "No owner yet",
          type: meta?.type || "unassigned", count: 0, oldest: 0, overdue: 0,
        });
        r.count += 1;
        r.oldest = Math.max(r.oldest, blockAgeDays(b) || 0);
        if (promiseState(b) === "overdue") { r.overdue += 1; od += 1; }
        blockedItems.add(it.id);
        n += 1;
      });
    });
    const order = { external: 0, internal: 1, unassigned: 2 };
    return {
      rollup: Object.values(acc).sort((a, b) => (order[a.type] - order[b.type]) || (b.count - a.count) || (b.oldest - a.oldest)),
      total: n, itemCount: blockedItems.size, overdue: od,
    };
  }, [items, catalogue]);

  const coloured = useOwnerColours(rollup);
  const shown = coloured.slice(0, MAX_ROWS);
  const hidden = coloured.length - shown.length;

  const maxCount = Math.max(1, ...shown.map((r) => r.count));

  // Click = the right edge presses in like a switch (same as the hero's
  // Overdue zone), then the board opens. Snaps straight through when
  // Performance Mode or reduced motion is on.
  const [pressing, setPressing] = useState(false);
  const open = useCallback(() => {
    const noMotion =
      document.documentElement.dataset.flat === "1" ||
      (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    if (noMotion) { navigate("/blocked-board"); return; }
    setPressing(true);
    setTimeout(() => navigate("/blocked-board"), 480);
  }, [navigate]);

  return (
    <button
      type="button"
      onClick={open}
      className={`group rl-open-card ${pressing ? "rl-press-right" : ""} w-full xl:w-[460px] min-[1400px]:w-[560px] xl:shrink-0 grid grid-cols-[124px_1fr] min-[1400px]:grid-cols-[140px_1fr] gap-x-5 text-left rounded-xl pl-7 pr-6 py-5 rl-glass rl-hero-glass focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/60`}
      data-testid="hero-blocked-widget"
      title="Open the Blocked Board"
    >
      {/* Left: built exactly like the hero's KPI stacks (label / 28px number /
          11px caption, extra line hung below) so "3" sits on the same lines as
          "20" across the row. Lifted 20px above centre (mb-10) to sit level
          with the owner list; the hero's stacks get the same lift. */}
      <div className="relative self-center mb-10 flex flex-col gap-0.5 pr-4" data-testid="hero-blocked-count">
        <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]/70 whitespace-nowrap">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${total > 0 ? "bg-red-500/75" : "bg-emerald-500/75"}`} />
          Blocked Board
        </span>
        <span className="font-mono-data text-[1.5rem] md:text-[1.75rem] md:mt-1 leading-none font-bold tracking-tight tabular-nums text-[hsl(var(--foreground))]">{itemCount}</span>
        <span className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 leading-tight">item{itemCount === 1 ? "" : "s"} blocked</span>
        {total > 0 && (
          <span className="absolute left-0 top-full mt-1.5 font-mono-data text-[10.5px] text-[hsl(var(--muted-foreground))]/75 whitespace-nowrap">{total} open block{total === 1 ? "" : "s"}</span>
        )}
      </div>

      {/* Right: who we're waiting on (ranked like the Blocked Board itself),
          then the footer. The divider runs the card's full height. */}
      <div className="flex flex-col gap-2.5 min-w-0 pl-5 -ml-5 border-l border-[hsl(var(--border))]/60">
        {total === 0 ? (
          /* The quiet day. Same footprint, so the hero never jumps between a
             blocked morning and a clear afternoon. */
          <div className="flex-1 flex flex-col items-center justify-center text-center py-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-500/80 mb-1.5" />
            <div className="text-[12.5px] font-medium text-[hsl(var(--foreground))]">Nothing is blocked</div>
            <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-0.5">Nobody to chase today.</div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col justify-center gap-[5px]" data-testid="hero-blocked-split">
            {shown.map((r) => (
              <div
                key={r.key}
                className="grid grid-cols-[minmax(0,104px)_1fr_20px_34px] items-center gap-2.5 text-[12px]"
                data-testid={`hero-blocked-owner-${r.owner || "none"}`}
                title={`${r.owner ? r.owner + " · " : ""}${r.label}: ${r.count} open, oldest ${r.oldest}d${r.overdue ? `, ${r.overdue} promise${r.overdue === 1 ? "" : "s"} slipped` : ""}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-2 h-2 rounded-[3px] shrink-0 ${r.type === "unassigned" ? "border border-dashed border-[hsl(var(--muted-foreground))]/70" : ""}`}
                    style={r.type === "unassigned" ? undefined : { background: r.color }}
                  />
                  <span className={`truncate ${r.type === "unassigned" ? "text-[hsl(var(--muted-foreground))]" : "text-[hsl(var(--foreground))]"}`}>
                    {r.type === "unassigned" ? "No owner" : r.label}
                  </span>
                </span>
                <span className="h-[5px] rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                  <span className="block h-full rounded-full" style={{ width: `${(r.count / maxCount) * 100}%`, background: r.color }} />
                </span>
                <span className={`font-mono-data text-[12.5px] font-bold tabular-nums text-right ${r.overdue ? "rl-slip" : "text-[hsl(var(--foreground))]"}`}>
                  {r.count}
                </span>
                <span className="font-mono-data text-[10.5px] text-[hsl(var(--muted-foreground))]/80 text-right whitespace-nowrap">{r.oldest}d</span>
              </div>
            ))}
          </div>
        )}

        <div className="pt-2 flex items-center justify-between gap-3 text-[11px] border-t border-[hsl(var(--border))]/45">
          <span className="flex items-center gap-3">
            {overdue > 0 && (
              <span className="rl-slip font-medium">{overdue} promise{overdue === 1 ? "" : "s"} slipped</span>
            )}
            {hidden > 0 && (
              <span className="text-[hsl(var(--muted-foreground))]">+ {hidden} more owner{hidden === 1 ? "" : "s"}</span>
            )}
          </span>
          {/* Same pill as the hero's "View →" chips; a quiet link at rest,
              dark glossy chip on hover. */}
          <span className="rl-open-chip red">
            Open board <ArrowRight className="rl-open-arrow w-3 h-3" strokeWidth={2.4} />
          </span>
        </div>
      </div>
    </button>
  );
}
